import { useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Button, Modal, Tabs } from "@mwi/ui";

import SpeciesTab from "./tabs/species";
import useErrorHandler from "hooks/useErrorHandler";
import {
  useGetPractitionerQuery,
  useGetReasonsQuery,
  useGetSpeciesQuery,
  useUpdatePractitionerMutation,
} from "api";
import { useSelector } from "react-redux";
import { RootState } from "store";
import ReasonsTab from "./tabs/reasons";
import { useToast } from "hooks/useToast";

type Inputs = {
  species_condition: {
    works_with: boolean | string | null;
    species: string[];
  };
  reasons_condition: {
    works_with: boolean | string | null;
    reasons: string[];
  };
};

type EditPractitionerModalTabs = "species" | "reasons";

type EditPractitionerModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const EditPractitionerModal = ({
  isOpen,
  onClose,
}: EditPractitionerModalProps): JSX.Element => {
  const { filtering } = useSelector((state: RootState) => ({
    filtering: state.practitionerFilters.filtering,
  }));

  const [selectedTab, setSelectedTab] = useState<EditPractitionerModalTabs>(
    filtering.category,
  );

  const { successToast, errorToast } = useToast();
  const errorHandler = useErrorHandler();

  const { practitionerId } = useParams();

  const {
    data: practitioner,
    isFetching: fetchingPractitioner,
    isError: errorFetchingPractitioner,
  } = useGetPractitionerQuery(
    {
      practitionerUuid: practitionerId!,
      locationUuid:
        filtering.site && filtering.category === "reasons"
          ? filtering.site
          : undefined,
    },
    {
      skip: !practitionerId,
    },
  );

  const { data: species, isLoading: loadingSpecies } = useGetSpeciesQuery(
    {},
    {
      skip: filtering.category !== "species",
    },
  );

  const { data: reasons, isLoading: loadingReasons } = useGetReasonsQuery(
    {
      locationUuid: filtering.site!,
      includeDisabled: false,
    },
    {
      skip: filtering.category !== "reasons" && !filtering?.site,
    },
  );

  const [
    updatePractitioner,
    {
      error: practitionerErrors,
      isLoading: updatingPractitioner,
      isSuccess: didUpdatePractitioner,
      isError: errorUpdatingPractitioner,
    },
  ] = useUpdatePractitionerMutation();

  const {
    control,
    handleSubmit,
    setValue,
    reset,
    setError,
    watch,
    formState: { errors },
  } = useForm<Inputs>();

  useEffect(() => {
    if (practitioner) {
      // Reasons
      setValue(
        "reasons_condition.works_with",
        practitioner.reasons_condition.reasons?.length
          ? practitioner.reasons_condition.works_with
            ? "does_work_with"
            : "doesnt_work_with"
          : null,
      );
      setValue(
        "reasons_condition.reasons",
        practitioner.reasons_condition?.reasons?.map((r) => r.reason_uuid),
      );

      // species
      setValue(
        "species_condition.works_with",
        practitioner.species_condition.species?.length
          ? practitioner.species_condition.works_with
            ? "does_work_with"
            : "doesnt_work_with"
          : null,
      );
      setValue(
        "species_condition.species",
        practitioner.species_condition.species.map((s) => s.species_uuid),
      );
    }
  }, [practitioner, practitionerId]);

  useEffect(() => {
    if (didUpdatePractitioner) {
      onClose();
      successToast({
        title: "Practitioner Updated",
        message: `${practitioner?.first_name ?? ""} ${practitioner?.last_name ?? ""} has been updated`,
      });
    }
  }, [didUpdatePractitioner]);

  useEffect(() => {
    if (errorUpdatingPractitioner) {
      errorHandler(practitionerErrors, setError);
    }

    if (errorFetchingPractitioner) {
      onClose();
      errorToast({
        title: "Unable to edit practitioner",
        message:
          "We're unable to update this practitioner at the moment. Please try again later.",
      });
    }
  }, [errorUpdatingPractitioner, errorFetchingPractitioner]);

  const handleSave = (data: Inputs) => {
    updatePractitioner({
      practitionerUuid: practitionerId!,
      body: {
        location_uuid: filtering.site!,
        contact_uuid: practitionerId!,
        species_condition: {
          works_with: data.species_condition.species?.length
            ? data.species_condition.works_with === "does_work_with"
              ? true
              : false
            : false,
          species: data.species_condition.species?.map((s) => ({
            species_uuid: s,
          })),
        },
        reasons_condition: {
          works_with: data.reasons_condition.reasons?.length
            ? data.reasons_condition.works_with === "does_work_with"
              ? true
              : false
            : false,
          reasons: data.reasons_condition.reasons?.map((r) => ({
            reason_uuid: r,
          })),
        },
      },
    });
  };

  const handleAfterLeave = () => {
    reset();
  };

  useEffect(() => {
    if (filtering.category) {
      setSelectedTab(filtering.category);
    }
  }, [filtering.category]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      className="flex flex-col tablet:!max-w-[776px]"
      loading={fetchingPractitioner || loadingSpecies || loadingReasons}
      afterLeave={handleAfterLeave}
    >
      <Modal.Header
        className="!pb-0 mb-16"
        title={`${practitioner?.first_name ?? ""} ${practitioner?.last_name ?? ""}`}
      />

      <Modal.Content>
        {practitioner && (
          <>
            <Tabs className="gap-x-10">
              {filtering.category === "species" && (
                <Tabs.Item
                  title="Species"
                  isActive={selectedTab === "species"}
                  onClick={() => setSelectedTab("species")}
                />
              )}
              {filtering.category === "reasons" && (
                <Tabs.Item
                  title="Reasons"
                  isActive={selectedTab === "reasons"}
                  onClick={() => setSelectedTab("reasons")}
                />
              )}
            </Tabs>

            <form
              className="flex flex-col justify-between flex-grow mt-12"
              onSubmit={handleSubmit(handleSave)}
            >
              {selectedTab === "species" && (
                <SpeciesTab
                  species={species?.data}
                  {...{ control, watch, errors }}
                />
              )}
              {selectedTab === "reasons" && (
                <ReasonsTab
                  reasons={reasons?.data}
                  {...{ control, watch, errors }}
                />
              )}

              <Button
                type="submit"
                className="max-w-[153px] mt-4"
                loading={updatingPractitioner}
                disabled={
                  (filtering.category === "species" &&
                    watch("species_condition.works_with") === null) ||
                  (filtering.category === "reasons" &&
                    watch("reasons_condition.works_with") === null)
                }
              >
                Save
              </Button>
            </form>
          </>
        )}
      </Modal.Content>
    </Modal>
  );
};

export default EditPractitionerModal;
